import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ENDPOINTS } from '../@config/endpoints';
import { CommentEntry, ServiceCaseEntry, ServiceCasePayload, ServiceCaseResult } from '../@models';
import { Pagination } from '../@config';
import { CreateServiceCase } from '../@models/create-service-case.model';
import { ServiceCaseAttachment } from '../@models/service-case-attachments.model';

@Injectable({
  providedIn: 'root'
})
export class ServiceCasesService {

  constructor(private http: HttpClient) { }

  getServiceCases(skipCount: number, searchText?: any): Observable<ServiceCaseResult> {
    const normalizedSearchText = this.normalizeSearchText(searchText);
    return this.http.get<ServiceCaseResult>(ENDPOINTS.getServiceCases(Pagination.maxResultCount, skipCount, normalizedSearchText)).pipe(
      map((respone: any) => respone?.result || []));
  }

  getAll(skipCount: number, searchText?: any): Observable<ServiceCaseEntry[]> {
    const normalizedSearchText = this.normalizeSearchText(searchText);
    return this.http.get<ServiceCaseEntry[]>(ENDPOINTS.getServiceCases(Pagination.maxResultCount, skipCount, normalizedSearchText)).pipe(
      map((respone: any) => respone?.result?.items || []));
  }

  getServiceCaseComments(serviceCaseId: number): Observable<ServiceCaseResult> {
    return this.http.get<ServiceCaseResult>(ENDPOINTS.getServiceCaseComments(serviceCaseId)).pipe(
      map((respone: any) => respone?.result || []));
  }

  createOrUpdateServiceCase(payload: ServiceCasePayload): Observable<any> {
    return this.http.post(ENDPOINTS.createOrUpdateServiceCase(), payload);
  }

  CreateNewServiceCase(payload: CreateServiceCase): Observable<any> {
    return this.http.post(ENDPOINTS.CreateNewServiceCase(), payload);
  }

  getServiceCaseFileTree(recordId: number): Observable<any> {
    return this.http.post(ENDPOINTS.getFileTree(), {
      isRecordTypeRoot: true,
      reAssignmentType: 8,
      treeType: 2,
      recordId,
      maxDepth: 2,
      includeArchived: false,
    });
  }

  uploadServiceCaseFile(parent: any, name: string, extension: string, base64: string): Observable<any> {
    return this.http.post(ENDPOINTS.uploadFile(), {
      name,
      extension,
      base64,
      parent,
      buildTreeFromParentOnly: true,
      maxDepth: 2,
    });
  }

  downloadServiceCaseFile(fileId: number): Observable<Blob> {
    return this.http.post(ENDPOINTS.downloadFile(fileId), { fileId }, {
      responseType: 'blob',
    });
  }

  /** Mobile-only: CapacitorHttp intercepts XHR and returns raw base64 as text. */
  downloadServiceCaseFileAsText(fileId: number): Observable<string> {
    return this.http.post(ENDPOINTS.downloadFile(fileId), { fileId }, {
      responseType: 'text',
    });
  }

  createServiceCaseComment(payload: CommentEntry): Observable<any> {
    return this.http.post(ENDPOINTS.createServiceCaseComment(), payload);
  }

  getTenantId():  Observable<any> {
    return this.http.get<any>(ENDPOINTS.getTenantId()).pipe(
      map((respone: any) => respone?.result || []));
  }

  getServiceCaseById(id: number): Observable<ServiceCaseEntry> {
    return this.http.get<ServiceCaseEntry>(ENDPOINTS.getServiceCaseById(id)).pipe(
      map((respone: any) => respone?.result));
  }

  getServiceCaseAttachmentsById(id: number, tenantId: number): Observable<ServiceCaseAttachment[]> {
    return this.http.get<ServiceCaseAttachment[]>(ENDPOINTS.getServiceCaseAttachmentsById(id, tenantId)).pipe(
      map((respone: any) => respone?.result || []));
  }

  private normalizeSearchText(searchText: any): string {
    if (typeof searchText === 'string') {
      return searchText;
    }

    if (typeof searchText?.payload === 'string') {
      return searchText.payload;
    }

    if (typeof searchText?.detail?.value === 'string') {
      return searchText.detail.value;
    }

    if (typeof searchText?.target?.value === 'string') {
      return searchText.target.value;
    }

    if (typeof searchText?.value === 'string') {
      return searchText.value;
    }

    return '';
  }

}
